import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
import Table from "@components/Table";
import { Box, Container } from "@mui/material";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import BackgroundImage from "@components/BackgroundImageCst";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import ButtonBackPage from "@components/ButtonBackPage";
import { ServicesURL } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { ActionsType } from "src/interfaces/Common";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const ManagePlanProjects: React.FC = () => {
  const { t } = useTranslation(["manage-projects-page"]);
  const { id, IdPlan } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.foundedTraining.managePlan.projects"]}`;

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const [idElementDeleted, setIdElementDeleted] = useState<string>();

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getAllProject
        .replace(":corporateId", id || "")
        .replace(":planId", IdPlan || "")}`,
      PagesID["corporate.foundedTraining.managePlan.projects"],
      filters[filterId],
    );

  const flatDataTable = dataTable?.output.map((el: any) => ({
    ...el,
    ...{
      plan: el.plan.id,
      projectId: el.id,
      projectCode: el.project_code,
      projectTitle: el.project_title,
    },
  }));

  const { trigger: triggerDelete } = useSWRMutation(
    `${ServicesURL.deleteProject
      .replace(":corporateId", id || "")
      .replace(":planId", IdPlan || "")
      .replace(":projectId", idElementDeleted || "")}`,
    (url: string) => fetcher(`${url}`, MethodType.DELETE),
  );

  const handleDelete = React.useCallback(async () => {
    if (idElementDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDelete();
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idElementDeleted),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdElementDeleted(undefined);
      }
    }
  }, [dataTable, idElementDeleted, mutate, triggerDelete]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete":
          return {
            ...action,
            callBack: (id: any) => setIdElementDeleted(id),
          };
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-skills-jobs.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={`${t("hero-title")}`} description={""} />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID["corporate.foundedTraining.managePlan.projects"]}
            data={flatDataTable || []}
            columns={columns || []}
            configurationTopToolbar={actionsTopToolbar}
            configurationActionForRow={configurationActionForRow}
            renderRowActions={customRenderRowActions}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!idElementDeleted}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setIdElementDeleted(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default ManagePlanProjects;
